import { useEffect, useState, useRef } from "react";
import arrow from "@svg-icons/arrow-with-line-down.component.svg";
import { Icon, IconProps } from "@components/Icon/Icon";
import { Box } from "@components/Box/Box";
import styles from "../styles.module.scss";
import classnames from 'classnames';

export interface TicketCalculatorDropdownOption {
	label: string;
	value: string;
	hint?: string;
	icon?: IconProps["svg"];
	default?: boolean;
}

interface TicketCalculatorDropdownProps {
	type: "single" | "multiple";
	defaultText?: string;
	label?: string;
	options: TicketCalculatorDropdownOption[];
	selectedValue: TicketCalculatorDropdownOption[];
	setSelectedValue: (value: TicketCalculatorDropdownOption[]) => void;
	dropdownId: string;
	openDropdownId: string | null;
	setOpenDropdownId: (id: string | null) => void;
}

const TicketCalculatorDropdown: React.FC<TicketCalculatorDropdownProps> = ({
	type,
	defaultText,
	label,
	options,
	selectedValue,
	setSelectedValue,
	dropdownId,
	openDropdownId,
	setOpenDropdownId
}) => {
	const isOpen = openDropdownId === dropdownId;
    
	const [focusedIndex, setFocusedIndex] = useState(-1);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const optionsRef = useRef<HTMLFieldSetElement>(null);
	const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {	
		if (type === "single") {
			setSelectedValue([options.find((option) => option.value === e.target.value) as TicketCalculatorDropdownOption]);
			setOpenDropdownId(null);
			// Return focus to button after selection
			setTimeout(() => {
				buttonRef.current?.focus();
			}, 0);
		} else {
			const selectedOption = options.find((option) => option.value === e.target.value) as TicketCalculatorDropdownOption;
			const isAlreadySelected = selectedValue.some((option) => option.value === e.target.value);
			
			if (isAlreadySelected) {
				// Remove the option if it's already selected
				setSelectedValue(selectedValue.filter((option) => option.value !== e.target.value));
			} else {
				// Add the option if it's not selected
				// For trailer types, remove any other trailer type first
				let updatedValue = [...selectedValue];
				
				// If this is a trailer selection, remove any existing trailer selections
				if (selectedOption.value === "trailer" || selectedOption.value === "trailer-tempo100") {
					updatedValue = selectedValue.filter((option) => 
						option.value !== "trailer" && option.value !== "trailer-tempo100"
					);
				}
				
				// Add the new selection
				setSelectedValue([...updatedValue, selectedOption]);
			}
		}
	};

	const getSelectedOptionIndex = () => {
		if (selectedValue.length === 0) return 0;
		return options.findIndex(option => option.value === selectedValue[0]?.value);
	};

	const handleToggle = () => {
		if (isOpen) {
			setOpenDropdownId(null);
			setFocusedIndex(-1);
		} else {
			setOpenDropdownId(dropdownId);
			// Focus the selected option when opening, or first option if none selected
			const selectedIndex = getSelectedOptionIndex();
			setTimeout(() => {
				setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
				// Focus the selected option element
				if (optionRefs.current[selectedIndex >= 0 ? selectedIndex : 0]) {
					optionRefs.current[selectedIndex >= 0 ? selectedIndex : 0]?.focus();
				}
			}, 0);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (!isOpen) {
			// When dropdown is closed, only handle Enter and Space to open
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				handleToggle();
			}
			return;
		}

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				const nextIndex = (focusedIndex + 1) % options.length;
				setFocusedIndex(nextIndex);
				// Focus the next option element
				setTimeout(() => {
					if (optionRefs.current[nextIndex]) {
						optionRefs.current[nextIndex]?.focus();
					}
				}, 0);
				break;
			case 'ArrowUp':
				e.preventDefault();
				const prevIndex = (focusedIndex - 1 + options.length) % options.length;
				setFocusedIndex(prevIndex);
				// Focus the previous option element
				setTimeout(() => {
					if (optionRefs.current[prevIndex]) {
						optionRefs.current[prevIndex]?.focus();
					}
				}, 0);
				break;
			case 'Enter':
			case ' ':
				e.preventDefault();
				if (focusedIndex >= 0 && focusedIndex < options.length) {
					const focusedOption = options[focusedIndex];
					// Create a synthetic change event
					const syntheticEvent = {
						target: {
							value: focusedOption.value
						}
					} as React.ChangeEvent<HTMLInputElement>;
					handleChange(syntheticEvent);
				}
				break;
			case 'Escape':
				e.preventDefault();
				setOpenDropdownId(null);
				setFocusedIndex(-1);
				buttonRef.current?.focus();
				break;
			case 'Tab':
				// Close dropdown on Tab
				setOpenDropdownId(null);
				setFocusedIndex(-1);
				break;
		}
	};

    

	const getSelectedValueOptions = (option: TicketCalculatorDropdownOption) => {
		return selectedValue.find((selectedOption) => selectedOption.value === option.value);
	};

    useEffect(() => {
        if (selectedValue.length === 0) {
            setSelectedValue(options.filter((option) => option.default === true) as TicketCalculatorDropdownOption[]);
        }
    }, []);

	// Handle clicks outside the dropdown
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setOpenDropdownId(null);
				setFocusedIndex(-1);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen, setOpenDropdownId]);

	// Reset focused index when dropdown opens/closes
	useEffect(() => {
		if (!isOpen) {
			setFocusedIndex(-1);
		} else {
			// Set focus to selected option when opening
			const selectedIndex = getSelectedOptionIndex();
			setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
		}
	}, [isOpen]);

	return (
		<div 
			className={styles.ticketcalculatorDropdown}
			ref={dropdownRef}
			onKeyDown={handleKeyDown}
		>
			{label && <p className={styles.ticketcalculatorDropdownLabel}>{label}</p>}
			<fieldset 
				className={classnames(
					styles.ticketcalculatorDropdownOptions,
					isOpen ? undefined : styles.ticketcalculatorDropdownOptionsClosed
				)}
				ref={optionsRef}
				role="listbox"
				aria-multiselectable={type === "multiple"}
				aria-label={label || "Dropdown options"}
			>
				{options.map((option, index) => (
					<div 
						className={styles.ticketcalculatorDropdownOption} 
						key={option.value}
						role="option"
						aria-selected={getSelectedValueOptions(option) ? true : false}
						tabIndex={isOpen ? (focusedIndex === index ? 0 : -1) : -1}
						ref={(el) => {
							optionRefs.current[index] = el;
						}}
						onFocus={() => setFocusedIndex(index)}
						onBlur={() => {
							// Only reset focus index if focus is moving outside the dropdown
							setTimeout(() => {
								if (!dropdownRef.current?.contains(document.activeElement)) {
									setFocusedIndex(-1);
								}
							}, 0);
						}}
					>
						<input 
							type={type === "single" ? "radio" : "checkbox"} 
							id={option.value} 
							name={option.value} 
							value={option.value} 
							checked={getSelectedValueOptions(option) ? true : false} 
							onChange={handleChange} 
							className="visuallyhidden" 
						/>
						<label 
							htmlFor={option.value}
							className={classnames(
								focusedIndex === index && isOpen ? styles.ticketcalculatorDropdownOptionFocused : ""
							)}
						>
							<Box className={classnames(styles.ticketcalculatorDropdownOptionIcon, !option.icon ? styles.hidden : undefined)}>
								<Icon svg={option.icon as IconProps["svg"]} />
							</Box>
							<p className={styles.ticketcalculatorDropdownOptionLabel}>{option.label}</p>
							<p className={styles.ticketcalculatorDropdownOptionHint}>{option.hint}</p>
						</label>
					</div>
				))}
			</fieldset>
			<button 
				type="button" 
				className={styles.ticketcalculatorDropdownButton} 
				onClick={handleToggle} 
				aria-expanded={isOpen} 
				aria-controls={dropdownId}
				aria-haspopup="listbox"
				ref={buttonRef}
			>
				<Box className={styles.ticketcalculatorDropdownButtonValue}>
					{selectedValue.length > 0 ? <>
						<Box className={classnames(styles.ticketcalculatorDropdownOptionIcon, !selectedValue[0]?.icon ? styles.hidden : undefined)}>
							<Icon svg={selectedValue[0]?.icon as IconProps["svg"]} />
						</Box>
						<p className={styles.ticketcalculatorDropdownOptionLabel}>{selectedValue[0]?.label}</p>
						{selectedValue[0]?.hint && <p className={styles.ticketcalculatorDropdownOptionHint}>{selectedValue[0]?.hint}</p>}
						{type === "multiple" && <p className={classnames(styles.ticketcalculatorDropdownOptionLabel, styles.ticketcalculatorDropdownOptionLabelCount, selectedValue.length > 1 ? "" : styles.ticketcalculatorDropdownOptionLabelHidden)} {...(selectedValue.length > 1 && {"aria-hidden": true})}>+ {selectedValue.length - 1}</p>}
					</> : <>
					<Box className={classnames(styles.ticketcalculatorDropdownOptionIcon, styles.hidden)} />
					{defaultText && <p>{defaultText}</p>}
					</>}
				</Box>
				<Icon svg={arrow} fill="white" className={classnames(styles.ticketcalculatorDropdownArrow, !isOpen ? styles.ticketcalculatorDropdownArrowClosed : "")} />
			</button>
		</div>
	);
};

export default TicketCalculatorDropdown;